import { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { ActionText } from '@components/action-text';
import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { WebhookCondition } from '@components/integrations/integrations-types';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';

import { routePaths } from 'ui/authenticated/navigation';
import { DescriptionFormField } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

type Data = {
  [key: string]: string;
};

export type WebhookConditionData = { [key: string]: string | VariableValue };

const Form = createForm<Data>();

type ConditionComponentProps = {
  description?: string;
  initialConditions: WebhookCondition[];
  variableGroups: FieldMappingGroup[];
  defaultData: { [key: string]: VariableValue };
  onSubmit(payload: WebhookConditionData, description: string): Promise<any>;
  onCancel(): void;
  fieldMappingId: string;
  integrationId: string;
};

export const WebhookConditionComponent = ({
  initialConditions,
  description,
  variableGroups,
  defaultData,
  onSubmit,
  onCancel,
  fieldMappingId,
  integrationId,
}: ConditionComponentProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const [conditions] = useState<WebhookCondition[]>(initialConditions);
  const isDirty = formStore.useIsDirty();

  const handleSubmit = async (data: Data) => {
    const payload: WebhookConditionData = {};
    conditions.forEach((condition) => {
      payload[condition.id] = data[condition.id];
    });
    return onSubmit(payload, data.description as string);
  };

  const integrationDetailUrl = generatePath(routePaths.integrationDetail, {
    integrationId: integrationId,
    '*': null,
  });

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('conditions')}>
          <Stack space={4}>
            {conditions.map((condition) => (
              <Form.FieldMappingTextEditor
                key={condition.id}
                id={condition.id}
                label={
                  (condition.field?.variables &&
                    Object.values(condition.field.variables)[0].label) ??
                  ''
                }
                variableGroups={variableGroups}
                fieldMappingId={fieldMappingId}
                defaultValue={defaultData[condition.id] ?? ''}
                validators={['required']}
                singleLine
                plaintext
              />
            ))}
            {conditions.length === 0 && (
              <Alert variant="info">
                <Text font="regular14" color="indigo100" text={t('conditionsMissing')} />
                <ActionText.ExternalLink
                  font="regular14"
                  color="indigo100"
                  href={integrationDetailUrl}
                  underline
                >
                  {t('configureHere')}
                </ActionText.ExternalLink>
              </Alert>
            )}
          </Stack>
        </HideContainer>
      </Stack>

      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button variant="secondary" text={t('core.cancel')} onClick={onCancel} />
          <Form.Submit variant="primary-blue" text={t('core.save')} disabled={!isDirty} />
        </ButtonGroup>
      </Sidebar.Footer>
    </FormWithUnsavedChangesPrompt>
  );
};

const locale = {
  en: {
    actionDetails: 'Action details',
    conditions: 'Conditions',
    conditionsMissing:
      'This webhook has no conditons configured. In order to use this action you need to go to the webhook settings and add them.',
    configureHere: 'Configure here',
  },
};
